import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Progress,
} from '@/components/ui';

export type StatTileSegment = {
  label: string;
  value: number;
  onSelect?: () => void;
  ariaLabel?: string;
};

export type StatTileProps = {
  title: string;
  value: number | string;
  description?: string;
  onSelect?: () => void;
  selectAriaLabel?: string;
  segments?: StatTileSegment[];
};

export function StatTile({
  title,
  value,
  description,
  onSelect,
  selectAriaLabel,
  segments = [],
}: StatTileProps) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  return (
    <Card data-stat-group={title}>
      <CardHeader className="space-y-2">
        <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
          {title}
        </p>
        {onSelect ? (
          <Button
            type="button"
            variant="ghost"
            className="h-auto justify-start p-0 text-2xl font-semibold"
            aria-label={selectAriaLabel}
            onClick={onSelect}
          >
            <span data-stat-title={title}>{value}</span>
          </Button>
        ) : (
          <span className="text-2xl font-semibold" data-stat-title={title}>
            {value}
          </span>
        )}
        {description && (
          <p className="text-muted-foreground text-sm">{description}</p>
        )}
      </CardHeader>
      {segments.length > 0 && (
        <CardContent className="space-y-3 pt-0">
          {segments.map((segment) => {
            const content = (
              <>
                <span>{segment.label}</span>
                <Badge variant="secondary">{segment.value}</Badge>
              </>
            );
            return (
              <div key={segment.label} className="space-y-1.5">
                {segment.onSelect ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="flex w-full justify-between px-0"
                    aria-label={segment.ariaLabel}
                    data-stat-segment={segment.label}
                    onClick={segment.onSelect}
                  >
                    {content}
                  </Button>
                ) : (
                  <div
                    className="flex items-center justify-between text-sm"
                    data-stat-segment={segment.label}
                  >
                    {content}
                  </div>
                )}
                <Progress
                  value={total === 0 ? 0 : (segment.value / total) * 100}
                  aria-label={`${segment.label} share`}
                />
              </div>
            );
          })}
        </CardContent>
      )}
    </Card>
  );
}
